import React, {useState, useEffect, useCallback} from 'react';
import {View, StyleSheet, ScrollView} from 'react-native';
import {Text, SegmentedButtons, ActivityIndicator} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import DatePickerInput from '../../components/common/DatePickerInput';
import {useReportStore} from '../../store/useReportStore';
import {colors, spacing} from '../../theme';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';

const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getDateRange = (period, refDate) => {
  const date = new Date(refDate);
  switch (period) {
    case 'daily':
      return {from: formatDate(date), to: formatDate(date)};
    case 'weekly': {
      const day = date.getDay();
      const start = new Date(date);
      start.setDate(date.getDate() - day);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return {from: formatDate(start), to: formatDate(end)};
    }
    case 'monthly': {
      const start = new Date(date.getFullYear(), date.getMonth(), 1);
      const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      return {from: formatDate(start), to: formatDate(end)};
    }
    case 'yearly': {
      const start = new Date(date.getFullYear(), 0, 1);
      const end = new Date(date.getFullYear(), 11, 31);
      return {from: formatDate(start), to: formatDate(end)};
    }
    default:
      return {from: formatDate(date), to: formatDate(date)};
  }
};

const PERIODS = [
  {value: 'daily', label: 'Daily'},
  {value: 'weekly', label: 'Weekly'},
  {value: 'monthly', label: 'Monthly'},
  {value: 'yearly', label: 'Yearly'},
];

// Green for money, blue for order counts, red for alerts
const METRIC_CONFIG = {
  totalSales: {icon: 'cash-multiple', bg: '#E8F5E9', accent: '#2E7D32'},
  totalPayments: {icon: 'cash-check', bg: '#E8F5E9', accent: '#2E7D32'},
  ordersReceived: {icon: 'package-variant', bg: colors.surface, accent: colors.primary},
  ordersDelivered: {icon: 'package-variant-closed-check', bg: colors.surface, accent: colors.primary},
  ordersPastDue: {icon: 'clock-alert-outline', bg: '#FFEBEE', accent: '#C62828'},
  ordersUpcoming: {icon: 'calendar-clock', bg: colors.surface, accent: colors.primary},
};

const MetricCard = ({label, value, metricKey}) => {
  const config = METRIC_CONFIG[metricKey] || {icon: 'chart-bar', bg: colors.background, accent: colors.primary};
  return (
    <View style={[styles.card, {backgroundColor: config.bg}]}>
      <View style={[styles.cardIconCircle, {backgroundColor: config.accent + '15'}]}>
        <Icon name={config.icon} size={18} color={config.accent} />
      </View>
      <View style={styles.cardText}>
        <Text style={[styles.cardValue, {color: config.accent}]}>{value}</Text>
        <Text style={styles.cardLabel}>{label}</Text>
      </View>
    </View>
  );
};

const ReportDashboardTab = ({orgUnitId}) => {
  const {t} = useTranslation();
  const {dashboard, isLoading, fetchDashboard} = useReportStore();
  const [period, setPeriod] = useState('monthly');
  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));

  const loadData = useCallback(() => {
    const {from, to} = getDateRange(period, selectedDate);
    fetchDashboard(from, to, orgUnitId);
  }, [period, selectedDate, orgUnitId, fetchDashboard]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const formatCurrency = (val) => {
    if (val == null) return '0';
    return Number(val).toLocaleString('en-IN');
  };

  const moneyMetrics = dashboard
    ? [
        {key: 'totalSales', label: t('report.totalSales'), value: `\u20B9${formatCurrency(dashboard.totalSales)}`},
        {key: 'totalPayments', label: t('report.totalPayments'), value: `\u20B9${formatCurrency(dashboard.totalPayments)}`},
      ]
    : [];

  const countMetrics = dashboard
    ? [
        {key: 'ordersReceived', label: t('report.ordersReceived'), value: String(dashboard.ordersReceived)},
        {key: 'ordersDelivered', label: t('report.ordersDelivered'), value: String(dashboard.ordersDelivered)},
        {key: 'ordersPastDue', label: t('report.ordersPastDue'), value: String(dashboard.ordersPastDue)},
        {key: 'ordersUpcoming', label: t('report.ordersUpcoming'), value: String(dashboard.ordersUpcoming)},
      ]
    : [];

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <SegmentedButtons
        value={period}
        onValueChange={setPeriod}
        buttons={PERIODS.map(p => ({...p, label: t(`report.${p.value}`)}))}
        style={styles.segmented}
      />

      <DatePickerInput
        label={t('report.selectDate')}
        value={selectedDate}
        onChangeText={setSelectedDate}
        style={styles.datePicker}
      />

      {isLoading ? (
        <ActivityIndicator style={styles.loader} size="large" color={colors.primary} />
      ) : dashboard ? (
        <>
          {/* Money metrics — full width */}
          <View style={styles.moneyRow}>
            {moneyMetrics.map((item) => {
              const config = METRIC_CONFIG[item.key];
              return (
                <View key={item.key} style={[styles.moneyCard, {backgroundColor: config.bg}]}>
                  <View style={styles.moneyCardTop}>
                    <View style={[styles.moneyIconCircle, {backgroundColor: config.accent + '15'}]}>
                      <Icon name={config.icon} size={18} color={config.accent} />
                    </View>
                    <Text style={[styles.moneyLabel, {color: config.accent}]}>{item.label}</Text>
                  </View>
                  <Text style={[styles.moneyValue, {color: config.accent}]}>{item.value}</Text>
                </View>
              );
            })}
          </View>

          {/* Count metrics — 2x2 grid */}
          <View style={styles.grid}>
            {countMetrics.map((item) => (
              <MetricCard
                key={item.key}
                metricKey={item.key}
                label={item.label}
                value={item.value}
              />
            ))}
          </View>
        </>
      ) : (
        <Text style={styles.noData}>{t('report.noReportData')}</Text>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  contentContainer: {padding: spacing.md},
  segmented: {marginBottom: spacing.md},
  datePicker: {marginBottom: spacing.md},
  loader: {marginTop: spacing.xl},
  // Money metrics — side by side, full width
  moneyRow: {
    flexDirection: 'row',
    gap: spacing.md,
    marginBottom: spacing.md,
  },
  moneyCard: {
    flex: 1,
    borderRadius: 12,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: '#C8E6C9',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.08,
    shadowRadius: 4,
  },
  moneyCardTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 6,
  },
  moneyIconCircle: {
    width: 30,
    height: 30,
    borderRadius: 15,
    justifyContent: 'center',
    alignItems: 'center',
  },
  moneyLabel: {
    fontSize: 12,
    fontWeight: '600',
  },
  moneyValue: {
    fontSize: 22,
    fontWeight: '800',
  },

  // Count metrics grid
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  card: {
    width: '46%',
    flexGrow: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    borderRadius: 12,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.08,
    shadowRadius: 4,
  },
  cardIconCircle: {
    width: 38,
    height: 38,
    borderRadius: 19,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardText: {
    flex: 1,
  },
  cardValue: {
    fontSize: 21,
    fontWeight: '800',
    lineHeight: 25,
  },
  cardLabel: {
    fontSize: 11,
    color: colors.textSecondary,
    fontWeight: '500',
    lineHeight: 14,
  },
  noData: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: spacing.xl,
  },
});

export default ReportDashboardTab;
